import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, DollarSign, TrendingUp } from 'lucide-react';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import { useToast } from '../../hooks/use-toast';

const EngineerStats = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/engineer/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <EngineerSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="text-center py-12">
            <p className="text-[#A6A6A6]">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  const totalRevenue = stats?.total_revenue || 0;
  const totalOrders = stats?.orders?.total || 0;
  const totalConsultings = stats?.consultings?.total || 0;
  const totalClients = stats?.clients?.length || 0;

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Statistiques</span> Globales
            </h1>
            <p className="text-[#A6A6A6]">Vue d'ensemble de tous les clients</p>
          </div>

          {/* Global Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Total Clients</p>
                    <p className="text-3xl font-bold text-white">{totalClients}</p>
                  </div>
                  <Users className="h-10 w-10 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Chiffre d'Affaires Total</p>
                    <p className="text-3xl font-bold text-[#C00000]">{totalRevenue.toFixed(2)}€</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                                  <p className="text-[#A6A6A6] text-sm mb-1">Tuning Files Totaux</p>
                    <p className="text-3xl font-bold text-white">{totalOrders}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Consultings Totaux</p>
                    <p className="text-3xl font-bold text-white">{totalConsultings}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Client Details */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardHeader>
              <CardTitle className="text-white">Détails par Client</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.clients && stats.clients.length > 0 ? (
                <div className="space-y-4">
                  {stats.clients.map((client, index) => (
                    <Card key={index} className="bg-[#0a0a0a] border-[#A6A6A6]/10">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-white font-bold text-lg">{client.client_name}</h3>
                            <p className="text-[#A6A6A6] text-sm">{client.client_email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-[#C00000]">{client.total_revenue.toFixed(2)}€</p>
                            <p className="text-[#A6A6A6] text-sm">{client.orders_count} commandes</p>
                            <p className="text-blue-400 text-sm">{client.consultings_count} consultings</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-[#1a1a1a] p-3 rounded">
                            <p className="text-[#A6A6A6] text-xs mb-1">CA Tuning Files</p>
                            <p className="text-white font-bold">{client.orders_revenue.toFixed(2)}€</p>
                          </div>
                          <div className="bg-[#1a1a1a] p-3 rounded">
                            <p className="text-[#A6A6A6] text-xs mb-1">CA Consultings</p>
                            <p className="text-blue-400 font-bold">{client.consultings_revenue.toFixed(2)}€</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-[#A6A6A6] text-center py-8">Aucun client pour le moment</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EngineerStats;